import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Gamepad2, Grid2x2, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import type { PlaceableEntity } from '../lib/scene';

/** The drag payload the canvas listens for. */
export const ENTITY_DRAG_TYPE = 'application/x-venue-entity';

interface UnplacedPanelProps {
  /** Only the entities on this floor that have no element yet. */
  unplaced: readonly PlaceableEntity[];
  /** Every entity on this floor, so the panel can say "6 of 8 placed". */
  total: number;
  onPlace: (entity: PlaceableEntity) => void;
  onPlaceMany: (entities: readonly PlaceableEntity[]) => void;
}

interface AreaGroup {
  areaId: string;
  areaName: string;
  entities: PlaceableEntity[];
}

function groupByArea(entities: readonly PlaceableEntity[]): AreaGroup[] {
  const groups = new Map<string, AreaGroup>();
  for (const entity of entities) {
    const existing = groups.get(entity.areaId);
    if (existing) existing.entities.push(entity);
    else groups.set(entity.areaId, { areaId: entity.areaId, areaName: entity.areaName, entities: [entity] });
  }
  return [...groups.values()].sort((a, b) => a.areaName.localeCompare(b.areaName));
}

/**
 * §38 — the bridge between setup and the map. A table created in Settings has no
 * map element, so without this list it exists operationally and is invisible on
 * the floor forever. Clicking or dragging one drops an element that is already
 * linked, rather than making the operator draw a shape and then hunt for the
 * entity in a picker.
 */
export function UnplacedPanel({ unplaced, total, onPlace, onPlaceMany }: UnplacedPanelProps) {
  const { t } = useTranslation();
  const groups = useMemo(() => groupByArea(unplaced), [unplaced]);
  const placed = total - unplaced.length;

  if (total === 0) {
    return (
      <p className="py-6 text-center text-xs text-ink-muted">
        {t('spaces.editor.unplaced.noEntities')}
      </p>
    );
  }

  if (unplaced.length === 0) {
    return (
      <p className="py-6 text-center text-xs text-ink-muted">
        {t('spaces.editor.unplaced.allPlaced', { count: total })}
      </p>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-ink-muted">
          {t('spaces.editor.unplaced.progress', { placed, total })}
        </span>
        <Button size="sm" variant="secondary" onClick={() => onPlaceMany(unplaced)}>
          <Plus className="h-4 w-4" />
          {t('spaces.editor.unplaced.placeAll', { count: unplaced.length })}
        </Button>
      </div>

      <p className="text-xs text-ink-muted">{t('spaces.editor.unplaced.hint')}</p>

      {groups.map((group) => (
        <div key={group.areaId} className="space-y-1.5">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-xs font-semibold">{group.areaName}</span>
            <button
              type="button"
              onClick={() => onPlaceMany(group.entities)}
              className="shrink-0 text-xs text-accent underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('spaces.editor.unplaced.placeArea', { count: group.entities.length })}
            </button>
          </div>

          <ul className="space-y-1">
            {group.entities.map((entity) => {
              const Icon = entity.kind === 'VenueTable' ? Grid2x2 : Gamepad2;
              return (
                <li key={entity.id}>
                  <button
                    type="button"
                    draggable
                    onDragStart={(event) => {
                      event.dataTransfer.setData(ENTITY_DRAG_TYPE, entity.id);
                      event.dataTransfer.setData('text/plain', entity.id);
                      event.dataTransfer.effectAllowed = 'copy';
                    }}
                    onClick={() => onPlace(entity)}
                    title={t('spaces.editor.unplaced.placeOne', { name: entity.label })}
                    className="flex w-full items-center gap-2 rounded-lg border border-amber-200 dark:border-amber-900 bg-amber-50/60 dark:bg-amber-950/50 px-2 py-1.5 text-start text-xs hover:bg-amber-100 dark:hover:bg-amber-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <Icon className="h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
                    <span className="min-w-0 flex-1 truncate font-medium">{entity.label}</span>
                    {entity.capacity != null && (
                      <Badge tone="neutral">
                        {entity.kind === 'VenueTable'
                          ? t('setup.table.seatCount', { count: entity.capacity })
                          : t('spaces.resources.capacity', { count: entity.capacity })}
                      </Badge>
                    )}
                    <Plus className="h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400" aria-hidden />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </div>
  );
}
